import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Papa from "papaparse";
import { toast } from "react-toastify";
import { getToken } from "../../../utils/tokenService";

const statuses = ["Present", "Absent", "Leave", "First Half Leave", "Second Half Leave"];

const ManualAttendance = () => {
  const [users, setUsers] = useState([]);
  const [date, setDate] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formRow, setFormRow] = useState({
    userId: "",
    status: "Present",
    checkInTime: "",
    checkOutTime: "",
    totalHours: "",
  });

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/establishment/active-users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUsers(res.data?.activeUsers || []);
      } catch (err) {
        toast.error("Failed to load users");
      }
    };
    loadUsers();
  }, []);

  const addRow = () => {
    if (!date) return toast.warn("Select date first");
    if (!formRow.userId) return toast.warn("Choose an employee");
    setRows((prev) => [...prev, { ...formRow, date }]);
  };

  const removeRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleCsv = (file) => {
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: ({ data }) => {
        const mapped = data
          .map((r) => ({
            userId: r.userId || r.user_id,
            employeeId: r.employeeId || r.employee_id,
            status: r.status || "Present",
            checkInTime: r.checkInTime || r.check_in || "",
            checkOutTime: r.checkOutTime || r.check_out || "",
            totalHours: r.totalHours || r.total_hours || "",
            date: r.date || date,
          }))
          .filter((r) => (r.userId || r.employeeId) && r.date);

        // Resolve employeeId -> userId when possible
        const enriched = mapped.map((r) => {
          if (!r.userId && r.employeeId) {
            const match = users.find(
              (u) => String(u.employeeId) === String(r.employeeId)
            );
            if (match) r.userId = match._id;
          }
          return r;
        });

        if (!enriched.length) {
          toast.warn("CSV empty or missing required columns");
        } else {
          setRows((prev) => [...prev, ...enriched]);
          toast.success(`Loaded ${enriched.length} rows from CSV`);
        }
      },
      error: () => toast.error("Could not read CSV"),
    });
  };

  const handleSubmit = async () => {
    if (!rows.length) return toast.warn("No rows to submit");
    try {
      setLoading(true);
      const token = await getToken();
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/establishment/attendance/manual-upload`,
        { records: rows },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Attendance uploaded");
      setRows([]);
      setFormRow((prev) => ({ ...prev, checkInTime: "", checkOutTime: "", totalHours: "" }));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        id: u._id,
        name: u.full_Name || u.email || u._id,
        employeeId: u.employeeId,
      })),
    [users]
  );

  return (
    <div className="container py-4">
      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <h5 className="mb-3">Upload Attendance Manually</h5>
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">Date</label>
              <input
                type="date"
                className="form-control"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Employee</label>
              <select
                className="form-select"
                value={formRow.userId}
                onChange={(e) => setFormRow((prev) => ({ ...prev, userId: e.target.value }))}
              >
                <option value="">Select</option>
                {userOptions.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={formRow.status}
                onChange={(e) => setFormRow((prev) => ({ ...prev, status: e.target.value }))}
              >
                {statuses.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Check-in</label>
              <input
                type="time"
                className="form-control"
                value={formRow.checkInTime}
                onChange={(e) => setFormRow((prev) => ({ ...prev, checkInTime: e.target.value ? `${date}T${e.target.value}` : "" }))}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Check-out</label>
              <input
                type="time"
                className="form-control"
                value={formRow.checkOutTime}
                onChange={(e) => setFormRow((prev) => ({ ...prev, checkOutTime: e.target.value ? `${date}T${e.target.value}` : "" }))}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Total Hours</label>
              <input
                type="number"
                min="0"
                step="0.1"
                className="form-control"
                value={formRow.totalHours}
                onChange={(e) => setFormRow((prev) => ({ ...prev, totalHours: e.target.value }))}
              />
            </div>
            <div className="col-md-12 d-flex gap-2">
              <button className="btn btn-primary" onClick={addRow}>
                Add Row
              </button>
              <label className="btn btn-outline-secondary mb-0">
                Upload CSV
                <input
                  type="file"
                  accept=".csv"
                  className="d-none"
                  onChange={(e) => handleCsv(e.target.files?.[0])}
                />
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Pending Rows ({rows.length})</h6>
            <button className="btn btn-success" onClick={handleSubmit} disabled={loading || !rows.length}>
              {loading ? "Uploading..." : "Submit"}
            </button>
          </div>
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Employee ID</th>
                  <th>Employee</th>
                  <th>Status</th>
                  <th>Check-in</th>
                  <th>Check-out</th>
                  <th>Hours</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {!rows.length && (
                  <tr>
                    <td colSpan={7} className="text-center text-muted">
                      No rows added yet.
                    </td>
                  </tr>
                )}
                {rows.map((r, idx) => {
                  const userMatch = userOptions.find((u) => u.id === r.userId);
                  const userName = userMatch?.name || r.userId || r.employeeId;
                  const empId = r.employeeId || userMatch?.employeeId || "-";
                  return (
                    <tr key={idx}>
                      <td>{r.date}</td>
                      <td>{empId}</td>
                      <td>{userName}</td>
                      <td>{r.status}</td>
                      <td>{r.checkInTime ? new Date(r.checkInTime).toLocaleTimeString() : "-"}</td>
                      <td>{r.checkOutTime ? new Date(r.checkOutTime).toLocaleTimeString() : "-"}</td>
                      <td>{r.totalHours || "-"}</td>
                      <td>
                        <button className="btn btn-link text-danger p-0" onClick={() => removeRow(idx)}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManualAttendance;
